import React from "react";
import { Node, Edge } from "reactflow";
import { Badge } from "../../../ui/badge";
import TruncatedText from "../components/TruncatedText";
import { VariablePlaceholder, GlobalVariableRegistry, InterjectionGroup, FlowData, DecisionNodeData, DecisionVariablesRegistry, VariableAssignment, SimpleFlowFormat, SimpleNode, EntityType, ToolCall } from "./types";

// Constants for node layout
export const NODE_WIDTH = 250;
export const HORIZONTAL_SPACING = 50; // Reduced from 75 for more compact horizontal spacing
export const VERTICAL_SPACING = 90; // Further reduced from 120 for more compact vertical spacing
export const PARENT_CHILD_FIXED_SPACING = 45; // Further reduced from 60 for closer parent-child spacing
export const DEFAULT_NODE_HEIGHT = 150;
export const MIN_SEPARATION = 35; // Reduced from 50 for tighter node separation
export const SUBTREE_SEPARATION = 70; // Reduced from 100 for closer subtrees
export const DISTANT_SUBTREE_SEPARATION = 100; // Reduced from 150 for closer distant subtrees

// In-memory storage for session data (no persistence across page refresh)
let inMemoryNodeIdCounter = 0;

// Initial data
const initialNodes: Node[] = [
  {
    id: "placeholder",
    type: "custom",
    position: { x: 250, y: 100 },
    data: {
      label: "Start Here",
      content: "Click to create your first node",
      prefix: "",
      suffix: "",
      jsonlogic: "",
      isPlaceholder: true,
      entityType: null,
      identifier: null,
      isDestinationNode: false,
      isDescendantOfDestination: false,
      isHighlighted: false,
      placeholders: {},
      globalRegistry: {},
      // These empty functions will be replaced by updateNodeHandlers
      onNodeResize: () => {},
      onPlaceholderClick: () => {},
      onAddNode: () => {},
      onEditPlaceholder: () => {},
      onEditNode: () => {},
      onDeleteNode: () => {},
      onContextMenu: () => {},
      onPreviewConversation: () => {}
    },
  },
];

const initialEdges: Edge[] = [];

// Function to get truly blank initial state (for reset functionality)
export const getBlankInitialState = () => {
  resetNodeIdCounter();
  
  return {
    nodes: initialNodes,
    edges: initialEdges,
    variables: {},
    decisionVariables: {},
    identifiers: [] as string[],
    isFirstNode: true,
    interjectionGroups: [] as any[]
  };
};

// Debug logging function
export const debugLog = (message: string) => {
  console.log(`[DEBUG] ConversationTree: ${message}`);
};

// Helper function to deduplicate edges by ID
export const deduplicateEdges = (edges: Edge[]): Edge[] => {
  const seenIds = new Set<string>();
  const uniqueEdges: Edge[] = [];
  let duplicatesRemoved = 0;
  
  edges.forEach(edge => {
    if (!seenIds.has(edge.id)) {
      seenIds.add(edge.id);
      uniqueEdges.push(edge);
    } else {
      duplicatesRemoved++;
      debugLog(`Removing duplicate edge during load: ${edge.id}`);
    }
  });
  
  if (duplicatesRemoved > 0) {
    debugLog(`Deduplicated ${duplicatesRemoved} edges during load`);
  }
  
  return uniqueEdges;
};

// Helper function to resolve identifier to node ID
export const resolveIdentifierToNodeId = (identifier: string, nodes: Node[]): string | null => {
  // First try to find by identifier
  const nodeByIdentifier = nodes.find(n => n.data.identifier === identifier);
  if (nodeByIdentifier) {
    return nodeByIdentifier.id;
  }
  
  // If not found by identifier, check if it's already a node ID (for backward compatibility)
  const nodeById = nodes.find(n => n.id === identifier);
  return nodeById ? nodeById.id : null;
};

// Function to extract placeholders from content
export const extractPlaceholders = (content: string): string[] => {
  const regex = /{([^{}]+)}/g;
  const matches = content.match(regex) || [];
  return matches.map(match => match.slice(1, -1));
};

// Function to render content with variable pills
export const renderContentWithPlaceholders = (
  content: string, 
  placeholders: Record<string, VariablePlaceholder>,
  globalRegistry: GlobalVariableRegistry,
  onEditPlaceholder: (name: string) => void
) => {
  if (!content) return null;
  
  // Split the content by the placeholder pattern
  const parts = content.split(/({[^{}]+})/);
  
  return React.createElement("span", { 
    className: "inline",
    style: { lineHeight: "1.4" }
  },
    parts.map((part, index) => {
      // Skip empty parts
      if (!part) return null;
      
      // Check if this part is a placeholder
      if (part.match(/^{[^{}]+}$/)) {
        const name = part.slice(1, -1); // Remove curly braces
        
        // Return styled pill for placeholder wrapped with truncation
        return React.createElement(Badge, {
          key: index,
          variant: "secondary",
          className: "cursor-pointer border border-maitai-green text-maitai-green/80 hover:bg-maitai-green/10 hover:text-maitai-green hover:border-maitai-green/30 transition-colors font-normal",
          style: { 
            verticalAlign: "baseline",
            lineHeight: "1.5",
            display: "inline-flex",
            alignItems: "center"
          },
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();
            onEditPlaceholder(name);
          }
        }, React.createElement(TruncatedText, { text: name, maxWidth: 160, className: "!inline-block align-middle" }));
      }
      
      // Return regular text that wraps normally  
      return React.createElement("span", {
        key: index,
        className: "text-foreground",
        style: { 
          verticalAlign: "baseline"
        }
      }, part);
    }).filter(Boolean)
  );
};

// Load saved data - now loads from localStorage if available, otherwise returns initial values
export const loadSavedData = () => {
  debugLog("Loading data (checking localStorage for auto-saved data)");
  
  // Try to load from localStorage first
  const savedFlow = loadFlowFromLocalStorage();
  if (savedFlow) {
    console.log("[ConversationTree] Found auto-saved data, restoring conversation tree", {
      nodeCount: savedFlow.data.nodes.length,
      edgeCount: savedFlow.data.edges.length,
      timestamp: savedFlow.timestamp
    });
    debugLog("Found auto-saved data, restoring conversation tree");
    // Sync the node ID counter with the loaded data
    syncNodeIdCounterWithExistingNodes(savedFlow.data.nodes);
    
    return {
      nodes: savedFlow.data.nodes,
      edges: savedFlow.data.edges,
      variables: savedFlow.data.globalVariableRegistry,
      decisionVariables: savedFlow.data.decisionVariablesRegistry,
      identifiers: savedFlow.data.identifiers,
      isFirstNode: savedFlow.data.isFirstNode,
      interjectionGroups: savedFlow.data.interjectionGroups || []
    };
  }
  
  // Fall back to initial/default values if no localStorage data
  debugLog("No auto-saved data found, loading initial data");
  resetNodeIdCounter();
  
  return {
    nodes: initialNodes,
    edges: initialEdges,
    variables: {},
    decisionVariables: {},
    identifiers: [] as string[],
    isFirstNode: true,
    interjectionGroups: []
  };
};

// Get children of a particular node
export const getNodeChildren = (nodeId: string, edges: Edge[]): string[] => {
  return edges
    .filter(edge => edge.source === nodeId)
    .map(edge => edge.target);
};

// Convert conversation path to JSON format
export const convertConversationToJson = (conversationPath: Node[], sessionId: string): string => {
  // Convert the nodes to messages
  const messages = conversationPath.map(node => {
    // Map entity type to role
    let role = "user";
    if (node.data.entityType === "BOT") {
      role = "assistant";
    }
    
    return {
      role,
      content: node.data.content
    };
  });
  
  // Add a system message at the beginning
  messages.unshift({
    role: "system",
    content: "\nRespond with conversation based on the tree structure."
  });
  
  // Construct the full JSON object - without tools section
  const jsonObject = {
    session_id: sessionId,
    messages
  };
  
  // Return the formatted JSON string with 2-space indentation
  return JSON.stringify(jsonObject, null, 2);
};

// Generate a random session ID
export const generateSessionId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// Node ID counter management - now uses in-memory storage only
export const getNextNodeId = (): number => {
  const current = getNodeIdCounter();
  const next = current + 1;
  
  setNodeIdCounter(next);
  return next;
};

export const getNodeIdCounter = (): number => {
  return inMemoryNodeIdCounter;
};

export const setNodeIdCounter = (counter: number): void => {
  inMemoryNodeIdCounter = counter;
};

export const resetNodeIdCounter = (): void => {
  setNodeIdCounter(0);
};

// Sync node ID counter with existing nodes to prevent conflicts
export const syncNodeIdCounterWithExistingNodes = (nodes: Node[]): void => {
  // Find the highest numeric ID among existing nodes
  let maxId = 0;
  nodes.forEach(node => {
    const nodeId = parseInt(node.id, 10);
    if (!isNaN(nodeId) && nodeId > maxId) {
      maxId = nodeId;
    }
  });
  
  // Only update if the max ID is higher than current counter
  const currentCounter = getNodeIdCounter();
  if (maxId > currentCounter) {
    // console.log(`🔄 Syncing node ID counter: ${currentCounter} -> ${maxId}`);
    setNodeIdCounter(maxId);
  }
};

// Flow Data Export/Import Functions

// Convert FlowData to SimpleFlowFormat for export
export const convertFlowDataToSimpleFormat = (flowData: FlowData): SimpleFlowFormat => {
  const { nodes, edges, globalVariableRegistry, decisionVariablesRegistry, interjectionGroups, systemPrompt } = flowData.data;
  
  // Find the start node (either marked as potentialStart or the first non-placeholder node)
  const startNode = nodes.find(n => n.data.potentialStart && !n.data.isPlaceholder) || 
                   nodes.find(n => !n.data.isPlaceholder);
  
  // Convert placeholders - separate regular from persistent
  const placeholders: { [key: string]: string[] | { values: string[]; persist?: boolean } } = {};
  Object.entries(globalVariableRegistry).forEach(([name, data]) => {
    if (data.persist) {
      placeholders[name] = {
        values: data.values || [],
        persist: true
      };
    } else {
      placeholders[name] = data.values || [];
    }
  });
  
  // Convert decision variables
  const decisionVariables = Object.entries(decisionVariablesRegistry).map(([name, data]) => ({
    name,
    type: data.type as "boolean" | "string" | "number"
  }));
  
  // Convert interjection groups
  const interjections = interjectionGroups?.map(group => ({
    type: group.type as "interruption" | "FAQ" | "other",
    customTypeName: group.type === "other" ? group.customTypeName : undefined,
    groupName: group.groupName,
    rate: group.interjection_rate,
    withinScript: group.within_script,
    repeatScript: group.repeat_script,
    content: group.content.map(item => ({
      text: item.interjection_text,
      response: item.interjection_response
    }))
  })).filter(group => group.content.length > 0);
  
  // Convert nodes
  const simpleNodes = nodes
    .filter(n => !n.data.isPlaceholder)
    .map(node => {
      const simpleNode: SimpleNode = {
        type: node.data.entityType.toLowerCase() as "bot" | "user" | "decision",
        identifier: node.data.identifier
      } as SimpleNode;
      
      // Only add content for non-decision nodes
      if (node.data.entityType !== "DECISION") {
        simpleNode.content = node.data.content || "";
      }
      
      // Add children (except for decision nodes which use truePath/falsePath)
      if (node.data.entityType !== "DECISION") {
        const childEdges = edges.filter(e => e.source === node.id);
        if (childEdges.length > 0) {
          simpleNode.children = childEdges.map(e => {
            const targetNode = nodes.find(n => n.id === e.target);
            return targetNode?.data.identifier || e.target;
          });
        }
      }
      
      // Add optional fields only if they have values
      // Only set conversationStart if this is a BOT node with potentialStart
      if (node.data.potentialStart && node.data.entityType === "BOT") {
        simpleNode.conversationStart = true;
      }
      
      if (node.data.isDestinationNode) {
        simpleNode.conversationEnd = true;
      }
      
      // Add tool calls
      if (node.data.toolCalls && node.data.toolCalls.length > 0) {
        simpleNode.toolCalls = node.data.toolCalls.map((tc: ToolCall) => ({
          name: tc.name,
          description: tc.description || undefined,
          parameters: tc.parameters?.reduce((acc: any, param: any) => {
            acc[param.name] = param.value;
            return acc;
          }, {}),
          responseVariables: tc.responseVariables?.reduce((acc: any, rv: any) => {
            acc[rv.name] = rv.values || [];
            return acc;
          }, {})
        }));
      }
      
      // Add variable assignments
      if (node.data.variableAssignments && node.data.variableAssignments.length > 0) {
        simpleNode.variableAssignments = {};
        node.data.variableAssignments.forEach((va: VariableAssignment) => {
          simpleNode.variableAssignments![va.variableName] = va.value;
        });
      }
      
      // Add decision-specific fields
      if (node.data.entityType === "DECISION" && node.data.decisionData) {
        const dd = node.data.decisionData;
        
        if (dd.conditions && dd.conditions.length > 0) {
          simpleNode.conditions = dd.conditions.map((cond: any, idx: number) => {
            const simpleCond: any = {
              variable: cond.variable,
              operator: cond.operator,
              value: cond.value
            };
            
            // Add type if it's boolean
            if (cond.type === "boolean") {
              simpleCond.type = "boolean";
            }
            
            // Add joiner for non-first conditions
            if (idx > 0 && cond.joiner) {
              simpleCond.joiner = cond.joiner;
            }
            
            return simpleCond;
          });
        }
        
        // Add paths using identifiers
        if (dd.truePath) {
          const trueNode = nodes.find(n => n.id === dd.truePath || n.data.identifier === dd.truePath);
          simpleNode.truePath = trueNode?.data.identifier || dd.truePath;
        }
        
        if (dd.falsePath) {
          const falseNode = nodes.find(n => n.id === dd.falsePath || n.data.identifier === dd.falsePath);
          simpleNode.falsePath = falseNode?.data.identifier || dd.falsePath;
        }
      }
      
      return simpleNode;
    });
  
  // Build the simple format
  const simpleFormat: SimpleFlowFormat = {
    title: flowData.metadata?.title || "Exported Conversation Tree",
    description: flowData.metadata?.description || `Exported on ${new Date().toLocaleString()}`,
    systemPrompt: systemPrompt || undefined,
    startNode: startNode?.data.identifier || "start",
    placeholders: Object.keys(placeholders).length > 0 ? placeholders : undefined,
    decisionVariables: decisionVariables.length > 0 ? decisionVariables : undefined,
    interjections: interjections && interjections.length > 0 ? interjections : undefined,
    nodes: simpleNodes
  };
  
  // Remove undefined fields
  return JSON.parse(JSON.stringify(simpleFormat));
};

// Export complete flow data to JSON format
export const exportFlowData = (
  nodes: Node[],
  edges: Edge[],
  globalVariableRegistry: GlobalVariableRegistry,
  decisionVariablesRegistry: DecisionVariablesRegistry,
  identifiers: string[],
  interjectionGroups: InterjectionGroup[],
  systemPrompt?: string, // Optional system prompt
  metadata?: Record<string, any>
): FlowData => {
  // Clean nodes: remove function references before serialization
  const cleanedNodes = nodes.map((node: Node) => ({
    ...node,
    data: {
      ...node.data,
      onNodeResize: undefined,
      onEditNode: undefined,
      onDeleteNode: undefined,
      onContextMenu: undefined,
      onPreviewConversation: undefined,
    }
  }));

  return {
    version: "1.0.0",
    timestamp: new Date().toISOString(),
    metadata: {
      status: "draft",
      ...metadata
    },
    data: {
      nodes: cleanedNodes,
      edges,
      globalVariableRegistry,
      decisionVariablesRegistry,
      identifiers,
      interjectionGroups,
      nodeIdCounter: getNodeIdCounter(),
      isFirstNode: nodes.length === 1 && nodes[0].data.isPlaceholder,
      systemPrompt: systemPrompt || undefined // Only include if not empty
    }
  };
};

// Helper function to check if data is in simple format
export const isSimpleFormat = (data: any): data is SimpleFlowFormat => {
  return (
    data &&
    typeof data === "object" &&
    data.startNode &&
    Array.isArray(data.nodes) &&
    !data.data && // Full format has a 'data' property
    !data.version // Full format has a 'version' property
  );
};

// Convert simple format to full FlowData format
// 
// Key distinction:
// - PLACEHOLDERS: {variable_name} in content fields -> go to globalVariableRegistry (need examples)
// - DECISION VARIABLES: used in conditions -> go to decisionVariablesRegistry (no examples needed)
// - VARIABLE ASSIGNMENTS: values nodes SET -> used by decision nodes later

// Validate simple format before conversion
export const validateSimpleFormat = (simpleFlow: SimpleFlowFormat): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  // Track which decision variables are assigned
  const assignedDecisionVars = new Set<string>();
  const usedInConditions = new Set<string>();
  const persistentPlaceholders = new Set<string>();
  const usedPlaceholders = new Set<string>(); // Track placeholders used in content
  const toolCallPlaceholders = new Set<string>(); // Track placeholders used in tool calls
  
  // Collect persistent placeholders and validate they have examples
  if (simpleFlow.placeholders) {
    Object.entries(simpleFlow.placeholders).forEach(([name, config]) => {
      if (!Array.isArray(config)) {
        if (config.persist) {
          persistentPlaceholders.add(name);
        }
        // Validate that each placeholder has at least one example
        if (!config.values || config.values.length === 0) {
          errors.push(`Placeholder "${name}" must have at least one example value`);
        }
      } else {
        // Legacy array format - validate it has at least one example
        if (config.length === 0) {
          errors.push(`Placeholder "${name}" must have at least one example value`);
        }
      }
    });
  }
  
  // Collect decision variables from decisionVariables array
  const declaredDecisionVars = new Set<string>();
  if (simpleFlow.decisionVariables) {
    simpleFlow.decisionVariables.forEach(dv => {
      declaredDecisionVars.add(dv.name);
    });
  }
  
  // Check all nodes
  simpleFlow.nodes.forEach(node => {
    // Extract placeholders from content
    if (node.content) {
      const contentPlaceholders = extractPlaceholders(node.content);
      contentPlaceholders.forEach(p => usedPlaceholders.add(p));
    }
    
    // Extract placeholders from tool calls (track separately)
    if (node.toolCalls) {
      node.toolCalls.forEach(toolCall => {
        if (toolCall.parameters) {
          Object.values(toolCall.parameters).forEach(paramValue => {
            const paramPlaceholders = extractPlaceholders(paramValue);
            paramPlaceholders.forEach(p => toolCallPlaceholders.add(p));
          });
        }
      });
    }
    
    // Validate node type-specific requirements
    if (node.type === "user") {
      // User nodes must have content
      if (!node.content || node.content.trim() === "") {
        errors.push(`User node "${node.identifier}": must have content`);
      }
    } else if (node.type === "bot") {
      // Bot nodes must have content OR tool calls (not both)
      const hasContent = node.content && node.content.trim() !== "";
      const hasToolCalls = node.toolCalls && node.toolCalls.length > 0;
      
      if (!hasContent && !hasToolCalls) {
        errors.push(`Bot node "${node.identifier}": must have either content or tool calls`);
      }
      if (hasContent && hasToolCalls) {
        errors.push(`Bot node "${node.identifier}": cannot have both content and tool calls`);
      }
    } else if (node.type === "decision") {
      // Decision nodes cannot have content
      if (node.content !== undefined && node.content !== null) {
        errors.push(`Decision node "${node.identifier}": cannot have content`);
      }
      // Decision nodes cannot have optional fields
      if (node.conversationEnd) {
        errors.push(`Decision node "${node.identifier}": cannot have conversationEnd`);
      }
      if (node.conversationStart) {
        errors.push(`Decision node "${node.identifier}": cannot have conversationStart`);
      }
    }
    
    // Nodes with conversationEnd cannot have children
    if (node.conversationEnd && node.children && node.children.length > 0) {
      errors.push(`Node "${node.identifier}": nodes with conversationEnd=true cannot have children`);
    }
    
    // Track variable assignments
    if (node.variableAssignments) {
      Object.keys(node.variableAssignments).forEach(varName => {
        assignedDecisionVars.add(varName);
      });
    }
    
    // Check decision node conditions
    if (node.type === "decision" && node.conditions) {
      node.conditions.forEach((condition, idx) => {
        usedInConditions.add(condition.variable);
        
        // Validate condition has required fields
        if (!condition.variable) {
          errors.push(`Decision node "${node.identifier}" condition ${idx + 1}: missing variable`);
        }
        if (!condition.operator) {
          errors.push(`Decision node "${node.identifier}" condition ${idx + 1}: missing operator`);
        }
        
        // Check joiner for non-first conditions
        if (idx > 0 && !condition.joiner) {
          errors.push(`Decision node "${node.identifier}" condition ${idx + 1}: missing joiner (and/or)`);
        }
      });
      
      // Check decision paths
      if (!node.truePath && !node.falsePath) {
        errors.push(`Decision node "${node.identifier}": must have at least one path (truePath or falsePath)`);
      }
    }
  });
  
  // Validate all variables used in conditions
  usedInConditions.forEach(varName => {
    // Must be either a persistent placeholder or declared decision variable
    if (!persistentPlaceholders.has(varName) && !declaredDecisionVars.has(varName)) {
      errors.push(`Variable "${varName}" used in conditions but not declared as persistent placeholder or decision variable`);
    }
    
    // If it's a placeholder, it must have persist: true
    if (simpleFlow.placeholders && simpleFlow.placeholders[varName]) {
      const config = simpleFlow.placeholders[varName];
      if (Array.isArray(config) || !config.persist) {
        errors.push(`Placeholder "${varName}" used in conditions must have persist: true`);
      }
    }
  });
  
  // Validate all used placeholders are properly declared
  usedPlaceholders.forEach(placeholderName => {
    const declaredAsPlaceholder = simpleFlow.placeholders && simpleFlow.placeholders.hasOwnProperty(placeholderName);
    const declaredAsDecisionVar = declaredDecisionVars.has(placeholderName);
    
    if (!declaredAsPlaceholder) {
      if (declaredAsDecisionVar) {
        // Special error for decision variables used as placeholders in content
        errors.push(`Decision variable "${placeholderName}" cannot be used as a placeholder in content (with {} syntax). Decision variables can only be used in conditions and tool parameters. Please define a placeholder with a different name and use that instead.`);
      } else {
        // Generic error for undeclared placeholders
        errors.push(`Placeholder "{${placeholderName}}" used in content but not declared in placeholders object`);
      }
    }
  });
  
  // Validate tool call placeholders - decision variables are allowed here
  toolCallPlaceholders.forEach(placeholderName => {
    const declaredAsPlaceholder = simpleFlow.placeholders && simpleFlow.placeholders.hasOwnProperty(placeholderName);
    const declaredAsDecisionVar = declaredDecisionVars.has(placeholderName);
    
    if (!declaredAsPlaceholder && !declaredAsDecisionVar) {
      // Must be declared as either a placeholder or decision variable
      errors.push(`Placeholder "{${placeholderName}}" used in tool parameters but not declared in placeholders object or as a decision variable`);
    }
  });
  
  // Check all declared decision variables are assigned
  declaredDecisionVars.forEach(varName => {
    // Skip if it's also a persistent placeholder (can be assigned externally)
    if (!persistentPlaceholders.has(varName) && !assignedDecisionVars.has(varName)) {
      // Check if it might come from tool response variables
      let foundInToolResponse = false;
      simpleFlow.nodes.forEach(node => {
        if (node.toolCalls) {
          node.toolCalls.forEach(tc => {
            if (tc.responseVariables && tc.responseVariables[varName]) {
              foundInToolResponse = true;
            }
          });
        }
      });
      
      if (!foundInToolResponse) {
        errors.push(`Decision variable "${varName}" declared but never assigned`);
      }
    }
  });
  
  // Check start node exists
  const nodeIdentifiers = simpleFlow.nodes.map(n => n.identifier);
  if (!nodeIdentifiers.includes(simpleFlow.startNode)) {
    errors.push(`Start node "${simpleFlow.startNode}" not found in nodes`);
  }
  
  // Check all node references exist (children, decision paths)
  simpleFlow.nodes.forEach(node => {
    if (node.children) {
      node.children.forEach(childId => {
        if (!nodeIdentifiers.includes(childId)) {
          errors.push(`Node "${node.identifier}" references non-existent child "${childId}"`);
        }
      });
    }
    
    if (node.type === "decision") {
      if (node.truePath && !nodeIdentifiers.includes(node.truePath)) {
        errors.push(`Decision node "${node.identifier}" references non-existent truePath "${node.truePath}"`);
      }
      if (node.falsePath && !nodeIdentifiers.includes(node.falsePath)) {
        errors.push(`Decision node "${node.identifier}" references non-existent falsePath "${node.falsePath}"`);
      }
    }
  });
  
  return {
    valid: errors.length === 0,
    errors
  };
};

export const convertSimpleFormatToFlowData = (simpleFlow: SimpleFlowFormat): FlowData => {
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const globalVariableRegistry: GlobalVariableRegistry = {};
  const decisionVariablesRegistry: DecisionVariablesRegistry = {};
  const identifiers: string[] = [];
  const interjectionGroups: InterjectionGroup[] = [];
  
  // Layout configuration
  const NODE_WIDTH = 300;
  const NODE_HEIGHT = 150;
  const HORIZONTAL_SPACING = 400;
  const VERTICAL_SPACING = 200;
  
  // Calculate positions using a simple tree layout
  const nodePositions: { [key: string]: { x: number; y: number } } = {};
  const processedNodes = new Set<string>();
  const nodeDepths: { [key: string]: number } = {};
  const nodesByDepth: { [depth: number]: string[] } = {};
  
  // First pass: calculate depths
  const calculateDepth = (nodeIdOrIdentifier: string, depth: number = 0) => {
    if (processedNodes.has(nodeIdOrIdentifier)) return;
    processedNodes.add(nodeIdOrIdentifier);
    nodeDepths[nodeIdOrIdentifier] = depth;
    
    if (!nodesByDepth[depth]) nodesByDepth[depth] = [];
    nodesByDepth[depth].push(nodeIdOrIdentifier);
    
    // Find node by ID or identifier
    const node = simpleFlow.nodes.find(n => 
      (n.id && n.id === nodeIdOrIdentifier) || 
      n.identifier === nodeIdOrIdentifier
    );
    if (node?.children) {
      node.children.forEach(childId => calculateDepth(childId, depth + 1));
    }
    if (node?.truePath) calculateDepth(node.truePath, depth + 1);
    if (node?.falsePath) calculateDepth(node.falsePath, depth + 1);
  };
  
  calculateDepth(simpleFlow.startNode);
  
  // Process interjections if provided
  if (simpleFlow.interjections) {
    simpleFlow.interjections.forEach((interjection, index) => {
      const interjectionGroup: InterjectionGroup = {
        id: `interjection-${index + 1}`,
        type: interjection.type || "interruption",
        customTypeName: interjection.customTypeName,
        groupName: interjection.groupName || `Interjection Group ${index + 1}`,
        interjection_rate: interjection.rate || 0.1,
        within_script: interjection.withinScript || false,
        repeat_script: interjection.repeatScript || false,
        content: interjection.content.map((item, contentIndex) => ({
          id: `interjection-${index + 1}-content-${contentIndex + 1}`,
          interjection_text: item.text,
          interjection_response: item.response
        }))
      };
      interjectionGroups.push(interjectionGroup);
    });
  }
  
  // Second pass: assign positions
  Object.keys(nodesByDepth).forEach(depthStr => {
    const depth = parseInt(depthStr);
    const nodesAtDepth = nodesByDepth[depth];
    const totalWidth = (nodesAtDepth.length - 1) * HORIZONTAL_SPACING;
    const startX = -totalWidth / 2;
    
    nodesAtDepth.forEach((nodeId, index) => {
      nodePositions[nodeId] = {
        x: startX + (index * HORIZONTAL_SPACING),
        y: depth * VERTICAL_SPACING
      };
    });
  });
  
  // Process global placeholders
  if (simpleFlow.placeholders) {
    Object.entries(simpleFlow.placeholders).forEach(([placeholderName, config]) => {
      if (Array.isArray(config)) {
        // Simple format: just array of values
        globalVariableRegistry[placeholderName] = {
          name: placeholderName,
          values: config,
          persist: false // Default to non-persistent
        };
      } else {
        // Full format with persist option
        globalVariableRegistry[placeholderName] = {
          name: placeholderName,
          values: config.values || [],
          persist: config.persist || false
        };
        
        // If it's a persistent placeholder, also add to decision variables registry
        // so it appears in the decision builder dropdown
        if (config.persist) {
          // Find the type from decisionVariables array if defined
          const decVarDef = simpleFlow.decisionVariables?.find(dv => dv.name === placeholderName);
          decisionVariablesRegistry[placeholderName] = {
            name: placeholderName,
            type: decVarDef?.type || "string" // Default to string if type not specified
          };
        }
      }
    });
  }
  
  // Process global decision variables that aren't already defined as placeholders
  if (simpleFlow.decisionVariables) {
    simpleFlow.decisionVariables.forEach(decVar => {
      // Only add to decision variables registry if not already added from persistent placeholders
      if (!decisionVariablesRegistry[decVar.name]) {
        decisionVariablesRegistry[decVar.name] = {
          name: decVar.name,
          type: decVar.type
        };
      }
    });
  }
  
  // First pass: Extract placeholders from content that might not be declared globally
  simpleFlow.nodes.forEach(simpleNode => {
    // Extract placeholders from content ({variable_name})
    const placeholderMatches = (simpleNode.content || "").match(/\{([^}]+)\}/g);
    if (placeholderMatches) {
      placeholderMatches.forEach(match => {
        const placeholderName = match.replace(/\{|\}/g, "");
        // Only add if not already in global registry and not a decision variable
        if (!globalVariableRegistry[placeholderName] && !decisionVariablesRegistry[placeholderName]) {
          globalVariableRegistry[placeholderName] = {
            name: placeholderName,
            values: [], // Empty array since no examples provided
            persist: false
          };
        }
      });
    }
    

    
    // Add tool call response variables to placeholder registry (not decision variables)
    if (simpleNode.toolCalls) {
      simpleNode.toolCalls.forEach(toolCall => {
        if (toolCall.responseVariables) {
          Object.entries(toolCall.responseVariables).forEach(([varName, values]) => {
            if (!globalVariableRegistry[varName]) {
              globalVariableRegistry[varName] = {
                name: varName,
                values: values || [],
                persist: false
              };
            } else {
              // Merge values if placeholder already exists
              const existingValues = globalVariableRegistry[varName].values || [];
              const mergedValues = Array.from(new Set([...existingValues, ...values]));
              globalVariableRegistry[varName].values = mergedValues;
            }
          });
        }
      });
    }
  });
  
    // Create a mapping from original references to generated node IDs
  const nodeIdMapping: { [key: string]: string } = {};

  // First pass: generate node IDs and create mapping
  simpleFlow.nodes.forEach((simpleNode, index) => {
    // Auto-generate node ID if not provided (use identifier or fallback to index)
    const nodeId = simpleNode.id || simpleNode.identifier || `node-${index + 1}`;

    // Create mapping for all possible references to this node
    if (simpleNode.id) nodeIdMapping[simpleNode.id] = nodeId;
    if (simpleNode.identifier) nodeIdMapping[simpleNode.identifier] = nodeId;
    nodeIdMapping[nodeId] = nodeId; // Self-mapping
  });
  
  // Convert nodes
  simpleFlow.nodes.forEach((simpleNode, index) => {
    // Auto-generate node ID if not provided (use identifier or fallback to index)
    const nodeId = simpleNode.id || simpleNode.identifier || `node-${index + 1}`;
    const position = nodePositions[nodeId] || { x: 0, y: index * VERTICAL_SPACING };
    
    // Add identifier to registry
    if (simpleNode.identifier && !identifiers.includes(simpleNode.identifier)) {
      identifiers.push(simpleNode.identifier);
    }
    
    // Convert tool calls
    const toolCalls: ToolCall[] = simpleNode.toolCalls ? simpleNode.toolCalls.map(tc => ({
      name: tc.name,
      description: tc.description || "",
      parameters: Object.entries(tc.parameters || {}).map(([name, value]) => ({ name, value })),
      responseVariables: tc.responseVariables ? 
        Object.entries(tc.responseVariables).map(([name, values]) => ({ name, values })) : []
    })) : [];
    
    // Convert variable assignments
    const variableAssignments: VariableAssignment[] = simpleNode.variableAssignments ? 
      Object.entries(simpleNode.variableAssignments).map(([variableName, value]) => ({
        variableName,
        value
      })) : [];
    
    // Convert decision data
    let decisionData: DecisionNodeData | undefined;
    if (simpleNode.type === "decision" && simpleNode.conditions) {
      // Operator mapping from user-friendly to internal values
      const operatorMapping: { [key: string]: string } = {
        "==": "equals",
        "!=": "not_equals", 
        "=": "equals",
        ">": "greater_than",
        "<": "less_than",
        ">=": "greater_than_or_equal",
        "<=": "less_than_or_equal",
        "contains": "contains",
        "not_contains": "not_contains",
        "starts_with": "starts_with", 
        "ends_with": "ends_with",
        "is_null": "is_null",
        "is_not_null": "is_not_null",
        "is_true": "is_true",
        "is_false": "is_false",
        // Also accept the internal values directly
        "equals": "equals",
        "not_equals": "not_equals",
        "greater_than": "greater_than",
        "less_than": "less_than",
        "greater_than_or_equal": "greater_than_or_equal",
        "less_than_or_equal": "less_than_or_equal"
      };

      decisionData = {
        conditions: simpleNode.conditions.map((cond, index) => ({
          id: `${nodeId}-condition-${index + 1}`, // Generate unique ID for each condition
          variable: cond.variable,
          operator: (operatorMapping[cond.operator] || cond.operator) as any, // Map to internal operator or use as-is
          value: cond.value || "",
          type: cond.type === "boolean" ? "boolean" : "value",
          joiner: index === 0 ? undefined : (cond.joiner || "and") // Use individual joiner or default to "and"
        })),
        logicType: "all", // Always set to "all" for compatibility
        truePath: simpleNode.truePath || null,
        falsePath: simpleNode.falsePath || null
      };
    }
    
    // Extract placeholders for this specific node
    const nodePlaceholders: Record<string, VariablePlaceholder> = {};
    const placeholderMatches = simpleNode.content.match(/\{([^}]+)\}/g);
    if (placeholderMatches) {
      placeholderMatches.forEach(match => {
        const placeholderName = match.replace(/\{|\}/g, "");
        // Get values from global registry if available
        const globalPlaceholder = globalVariableRegistry[placeholderName];
        if (globalPlaceholder) {
          nodePlaceholders[placeholderName] = {
            name: placeholderName,
            values: globalPlaceholder.values || [],
            persist: false
          };
        }
      });
    }

    // Create ReactFlow node
    const node: Node = {
      id: nodeId,
      type: "custom",
      position,
      data: {
        label: simpleNode.content,
        content: simpleNode.content,
        entityType: simpleNode.type.toUpperCase() as EntityType,
        identifier: simpleNode.identifier,
        isPlaceholder: false,
        potentialStart: simpleNode.conversationStart || nodeId === simpleFlow.startNode || simpleNode.identifier === simpleFlow.startNode,
        placeholders: nodePlaceholders,
        toolCalls,
        variableAssignments,
        decisionData,
        // Optional fields
        isDestinationNode: simpleNode.conversationEnd || false
      }
    };
    
    nodes.push(node);
    
    // Create edges from children (map child references to actual node IDs)
    if (simpleNode.children) {
      simpleNode.children.forEach(childReference => {
        const targetNodeId = nodeIdMapping[childReference] || childReference;
        edges.push({
          id: `e${nodeId}-${targetNodeId}`,
          source: nodeId,
          target: targetNodeId,
          sourceHandle: "source",
          type: "custom"
        });
      });
    }
    
    // Create edges for decision nodes (map path references to actual node IDs)
    if (simpleNode.truePath) {
      const trueTargetId = nodeIdMapping[simpleNode.truePath] || simpleNode.truePath;
      edges.push({
        id: `e${nodeId}-${trueTargetId}`,
        source: nodeId,
        target: trueTargetId,
        sourceHandle: "true",
        type: "custom",
        data: { isTrue: true }
      });
    }
    if (simpleNode.falsePath) {
      const falseTargetId = nodeIdMapping[simpleNode.falsePath] || simpleNode.falsePath;
      edges.push({
        id: `e${nodeId}-${falseTargetId}`,
        source: nodeId,
        target: falseTargetId,
        sourceHandle: "false",
        type: "custom",
        data: { isTrue: false }
      });
    }
  });
  
  return {
    version: "1.0.0",
    timestamp: new Date().toISOString(),
    metadata: {
      title: simpleFlow.title,
      description: simpleFlow.description,
      status: "draft" as const
    },
    data: {
      nodes,
      edges,
      globalVariableRegistry,
      decisionVariablesRegistry,
      identifiers,
      interjectionGroups,
      nodeIdCounter: nodes.length,
      isFirstNode: nodes.length === 1,
      systemPrompt: simpleFlow.systemPrompt
    }
  };
};

// Helper function to find node identifier by ID
const findNodeIdentifierById = (nodeId: string, nodes: Node[]): string | null => {
  const node = nodes.find(n => n.id === nodeId);
  return node?.data.identifier || null;
};

// Import flow data from JSON format
export const importFlowData = (flowData: FlowData) => {
  // Create a mapping from old node IDs to new sequential IDs
  const nodeIdMapping: Record<string, string> = {};
  let nodeCounter = 1;
  
  // First pass: Generate new sequential IDs for all nodes and migrate old classification fields
  const restoredNodes = flowData.data.nodes.map((node: Node) => {
    const newId = nodeCounter.toString();
    nodeIdMapping[node.id] = newId;
    nodeCounter++;
    
    // Migrate old classification field to identifier field
    const nodeData = { ...node.data };
    if ((nodeData as any).classification && !nodeData.identifier) {
      nodeData.identifier = (nodeData as any).classification;
      delete (nodeData as any).classification;
    }
    
    return {
      ...node,
      id: newId,
      data: {
        ...nodeData,
        onNodeResize: () => {},
        onPlaceholderClick: () => {},
        onAddNode: () => {},
        onEditPlaceholder: () => {},
        onEditNode: () => {},
        onDeleteNode: () => {},
        onContextMenu: () => {},
        onPreviewConversation: () => {}
      },
    };
  });

  // Second pass: Update decision node references
  // IMPORTANT: Decision paths should contain identifiers, not node IDs
  // The dropdown expects identifiers, so we need to preserve them
  const nodesWithUpdatedDecisions = restoredNodes.map((node: Node) => {
    if (node.data.decisionData && node.data.entityType === "DECISION") {
      // Check if the paths look like old node IDs that need mapping
      const truePath = node.data.decisionData.truePath;
      const falsePath = node.data.decisionData.falsePath;
      
      // Only remap if the path is a pure numeric ID (old format)
      // If it contains any non-numeric characters, it's likely an identifier
      const updatedDecisionData = {
        ...node.data.decisionData,
        truePath: truePath && /^\d+$/.test(truePath) && nodeIdMapping[truePath]
          ? findNodeIdentifierById(nodeIdMapping[truePath], restoredNodes) || truePath
          : truePath,
        falsePath: falsePath && /^\d+$/.test(falsePath) && nodeIdMapping[falsePath]
          ? findNodeIdentifierById(nodeIdMapping[falsePath], restoredNodes) || falsePath
          : falsePath
      };
      
      return {
        ...node,
        data: {
          ...node.data,
          decisionData: updatedDecisionData
        }
      };
    }
    return node;
  });

  // Regenerate all edge IDs with new node IDs
  const restoredEdges = flowData.data.edges.map((edge: Edge) => {
    const newSourceId = nodeIdMapping[edge.source];
    const newTargetId = nodeIdMapping[edge.target];
    
    const targetNode = restoredNodes.find(n => n.id === newTargetId);
    return {
      ...edge,
      id: `e${newSourceId}-${newTargetId}`,
      source: newSourceId,
      target: newTargetId,
      type: "custom",
      sourceHandle: edge.sourceHandle || (edge.id.endsWith("_true") ? "true" : (edge.id.endsWith("_false") ? "false" : "source")),
      targetHandle: edge.targetHandle || "target",
      data: (targetNode?.data as any)?.isDestinationNode ? { toDestination: true } : (edge.data || undefined)
    };
  });

  // Update decision edges for decision nodes with identifier resolution
  const additionalDecisionEdges: Edge[] = [];
  nodesWithUpdatedDecisions.forEach((node: Node) => {
    if (node.data.decisionData && node.data.entityType === "DECISION") {
      const { truePath, falsePath } = node.data.decisionData;
      
      if (truePath) {
        const truePathNodeId = resolveIdentifierToNodeId(truePath, nodesWithUpdatedDecisions);
        if (truePathNodeId) {
          additionalDecisionEdges.push({
            id: `e${node.id}-${truePathNodeId}_true`,
            source: node.id,
            target: truePathNodeId,
            sourceHandle: "true",
            targetHandle: "target",
            type: "custom",
            label: "True",
            style: { stroke: "#52c41a", strokeWidth: 2 },
            labelStyle: { fill: "#52c41a", fontWeight: 600 },
            labelBgStyle: { fill: "#f6ffed" },
            data: undefined
          });
        }
      }
      
      if (falsePath) {
        const falsePathNodeId = resolveIdentifierToNodeId(falsePath, nodesWithUpdatedDecisions);
        if (falsePathNodeId) {
          additionalDecisionEdges.push({
            id: `e${node.id}-${falsePathNodeId}_false`,
            source: node.id,
            target: falsePathNodeId,
            sourceHandle: "false",
            targetHandle: "target",
            type: "custom",
            label: "False",
            style: { stroke: "#f5222d", strokeWidth: 2 },
            labelStyle: { fill: "#f5222d", fontWeight: 600 },
            labelBgStyle: { fill: "#fff2f0" },
            data: undefined
          });
        }
      }
    }
  });

  // Combine regular edges with decision edges
  const allEdges = [...restoredEdges, ...additionalDecisionEdges];
  
  // Deduplicate edges before returning
  const deduplicatedEdges = deduplicateEdges(allEdges);

  // Update node ID counter to the next available ID
  setNodeIdCounter(nodeCounter - 1);

  // Backwards compatibility: accept both 'identifiers' and 'classifications'
  const importedIdentifiers = flowData.data.identifiers || (flowData.data as any).classifications || [];
  
  return {
    nodes: nodesWithUpdatedDecisions,
    edges: deduplicatedEdges,
    variables: flowData.data.globalVariableRegistry,
    decisionVariables: flowData.data.decisionVariablesRegistry || {},
    identifiers: importedIdentifiers,
    interjectionGroups: flowData.data.interjectionGroups || [],
    isFirstNode: nodesWithUpdatedDecisions.length === 1 && nodesWithUpdatedDecisions[0].data.isPlaceholder,
    systemPrompt: flowData.data.systemPrompt || "" // Import system prompt
  };
};

// Check if current state has meaningful content that can be saved
export const hasUnsavedChanges = (
  currentNodes: Node[],
  currentEdges: Edge[],
  currentGlobalVariableRegistry: GlobalVariableRegistry,
  currentDecisionVariablesRegistry: DecisionVariablesRegistry,
  currentIdentifiers: string[],
  currentInterjectionGroups: InterjectionGroup[]
): boolean => {
  // Simply check if we have any meaningful content that can be saved
  // This allows users to save whenever they want, even if nothing has changed
  const hasContent = currentNodes.some(node => !node.data.isPlaceholder) ||
                    currentEdges.length > 0 ||
                    Object.keys(currentGlobalVariableRegistry).length > 0 ||
                    currentIdentifiers.length > 0 ||
                    currentInterjectionGroups.length > 0;
  
  return hasContent;
};

// localStorage key for conversation tree auto-save
const CONVERSATION_TREE_STORAGE_KEY = "maitai-conversation-tree-autosave";

// Save flow data to localStorage for auto-save functionality
export const saveFlowToLocalStorage = (flowData: FlowData): void => {
  try {
    const serializedData = JSON.stringify(flowData);
    localStorage.setItem(CONVERSATION_TREE_STORAGE_KEY, serializedData);
    debugLog(`Flow data auto-saved to localStorage at ${flowData.timestamp}`);
  } catch (error) {
    console.error("Failed to save flow data to localStorage:", error);
  }
};

// Load flow data from localStorage
export const loadFlowFromLocalStorage = (): FlowData | null => {
  try {
    const savedData = localStorage.getItem(CONVERSATION_TREE_STORAGE_KEY);
    if (savedData) {
      const flowData = JSON.parse(savedData) as FlowData;
      debugLog(`Flow data loaded from localStorage (saved at ${flowData.timestamp})`);
      return flowData;
    }
    debugLog("No saved flow data found in localStorage");
    return null;
  } catch (error) {
    console.error("Failed to load flow data from localStorage:", error);
    return null;
  }
};

// Clear auto-saved data from localStorage
export const clearAutoSavedData = (): void => {
  try {
    localStorage.removeItem(CONVERSATION_TREE_STORAGE_KEY);
    debugLog("Auto-saved flow data cleared from localStorage");
  } catch (error) {
    console.error("Failed to clear auto-saved data:", error);
  }
};

// Helper function for testing auto-save functionality from browser console
export const testAutoSave = () => {
  console.log("=== ConversationTree Auto-Save Test ===");
  
  // Check if localStorage is available
  try {
    localStorage.setItem("test", "test");
    localStorage.removeItem("test");
    console.log("✅ localStorage is available");
  } catch (error) {
    console.error("localStorage is not available:", error);
    return;
  }
  
  // Check if auto-save data exists
  const savedData = localStorage.getItem(CONVERSATION_TREE_STORAGE_KEY);
  if (savedData) {
    try {
      const flowData = JSON.parse(savedData);
      console.log("✅ Auto-saved data found:", {
        timestamp: flowData.timestamp,
        nodeCount: flowData.data?.nodes?.length || 0,
        edgeCount: flowData.data?.edges?.length || 0,
        dataSize: `${(savedData.length / 1024).toFixed(2)} KB`
      });
    } catch (error) {
      console.error("Auto-saved data is corrupted:", error);
    }
  } else {
    console.log("ℹ️  No auto-saved data found");
  }
  
  console.log("=== End Test ===");
};

// Helper to manually save test data
export const testSaveData = () => {
  const testData = {
    version: "1.0",
    timestamp: new Date().toISOString(),
    metadata: { status: "draft" as const, title: "Test Save" },
    data: {
      nodes: [
        { id: "test1", type: "custom", position: { x: 0, y: 0 }, data: { label: "Test Node", isPlaceholder: false } }
      ] as any[],
      edges: [] as any[],
      globalVariableRegistry: {} as any,
      decisionVariablesRegistry: {} as any,
      identifiers: [] as string[],
      interjectionGroups: [] as any[],
      nodeIdCounter: 1,
      isFirstNode: false
    }
  };
  
  localStorage.setItem(CONVERSATION_TREE_STORAGE_KEY, JSON.stringify(testData));
  console.log("✅ Test data saved to localStorage");
  return testData;
};

// Helper to clear auto-save data
export const clearTestData = () => {
  localStorage.removeItem(CONVERSATION_TREE_STORAGE_KEY);
  console.log("✅ Auto-save data cleared");
};

// Helper to manually trigger auto-save for testing
export const testManualAutoSave = () => {
  console.log("=== Manual Auto-Save Test ===");
  
  // Create a simple test tree
  const testFlow = {
    version: "1.0",
    timestamp: new Date().toISOString(),
    metadata: { status: "draft" as const, title: "Manual Test" },
    data: {
      nodes: [
        {
          id: "node1",
          type: "custom",
          position: { x: 100, y: 100 },
          data: {
            label: "Test Node",
            content: "This is a test node for auto-save",
            isPlaceholder: false,
            entityType: "BOT"
          }
        }
      ] as any[],
      edges: [] as any[],
      globalVariableRegistry: { testVar: { name: "testVar", examples: ["test"] } } as any,
      decisionVariablesRegistry: {} as any,
      identifiers: ["test-id"] as string[],
      interjectionGroups: [] as any[],
      nodeIdCounter: 1,
      isFirstNode: false
    }
  };
  
  try {
    localStorage.setItem(CONVERSATION_TREE_STORAGE_KEY, JSON.stringify(testFlow));
    console.log("✅ Manual test data saved successfully");
    console.log("🔄 Now refresh the page to test loading");
    return testFlow;
  } catch (error) {
    console.error("Failed to save test data:", error);
  }
};

// Helper to check current cache status
export const checkTreeCache = () => {
  console.log("=== Tree Cache Status ===");
  try {
    const cached = localStorage.getItem("maitai-conversation-tree-cache");
    if (cached) {
      const data = JSON.parse(cached);
      console.log("✅ Cache found:", {
        timestamp: data.timestamp,
        nodeCount: data.nodes?.length || 0,
        edgeCount: data.edges?.length || 0,
        hasRealNodes: data.nodes?.some((n: any) => !n.data?.isPlaceholder) || false,
        size: `${(cached.length / 1024).toFixed(1)}KB`
      });
    } else {
      console.log("No cache found");
    }
  } catch (error) {
    console.error("Cache corrupted:", error);
  }
};

// Make test function available globally for console testing
if (typeof window !== "undefined") {
  (window as any).testConversationTreeAutoSave = testAutoSave;
  (window as any).saveTestTreeData = testSaveData;
  (window as any).clearTreeTestData = clearTestData;
  (window as any).testManualAutoSave = testManualAutoSave;
  (window as any).checkTreeCache = checkTreeCache;
}

// Decision node execution logic - evaluates single condition and returns true/false path
export const executeDecisionNode = (
  decisionData: DecisionNodeData,
  variables: Record<string, any>
): string | null => {
  const { conditions, truePath, falsePath } = decisionData;
  
  // Guard: Check if conditions array is empty
  if (!conditions || conditions.length === 0) {
    return falsePath;
  }
  
  const variableValue = variables[conditions[0].variable];
  
  // If variable doesn't exist, return false path
  if (variableValue === undefined || variableValue === null) {
    return falsePath;
  }
  
  let conditionResult = false;
  
  switch (conditions[0].operator) {
    case "is_true":
      conditionResult = Boolean(variableValue) === true;
      break;
    case "is_false":
      conditionResult = Boolean(variableValue) === false;
      break;
    case "==":
    case "equals":
      conditionResult = String(variableValue) === String(conditions[0].value);
      break;
    case "!=":
    case "not_equals":
      conditionResult = String(variableValue) !== String(conditions[0].value);
      break;
    case "contains":
      conditionResult = String(variableValue).toLowerCase().includes(String(conditions[0].value).toLowerCase());
      break;
    case ">":
    case "greater_than":
      conditionResult = Number(variableValue) > Number(conditions[0].value);
      break;
    case "<":
    case "less_than":
      conditionResult = Number(variableValue) < Number(conditions[0].value);
      break;
    case ">=":
    case "greater_than_or_equal":
      conditionResult = Number(variableValue) >= Number(conditions[0].value);
      break;
    case "<=":
    case "less_than_or_equal":
      conditionResult = Number(variableValue) <= Number(conditions[0].value);
      break;
  }
  
  // Return appropriate path based on condition result
  return conditionResult ? truePath : falsePath;
};

// Generate unique rule ID
export const generateRuleId = (): string => {
  return `rule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Manage decision node edges - creates/updates/removes edges based on decision paths
export const updateDecisionNodeEdges = (
  decisionNodeId: string,
  decisionData: DecisionNodeData,
  currentEdges: Edge[],
  setEdges: (edges: Edge[] | ((prev: Edge[]) => Edge[])) => void,
  nodes: Node[]
): void => {
  debugLog(`[updateDecisionNodeEdges] Updating edges for decision node ${decisionNodeId}`);
  debugLog(`[updateDecisionNodeEdges] Current edges count: ${currentEdges.length}`);
  
  // Remove ALL existing edges from this decision node (not just decision-labeled ones)
  // Decision nodes should only have decision edges, so we remove everything and rebuild
  const edgesToRemove = currentEdges.filter(edge => edge.source === decisionNodeId);
  
  debugLog(`[updateDecisionNodeEdges] Found ${edgesToRemove.length} edges to remove from decision node: ${JSON.stringify(edgesToRemove.map(e => e.id))}`);
  
  const nonDecisionNodeEdges = currentEdges.filter(edge => edge.source !== decisionNodeId);
  
  debugLog(`[updateDecisionNodeEdges] After filtering, ${nonDecisionNodeEdges.length} edges remain`);
  
  // Create new decision edges
  const newDecisionEdges: Edge[] = [];
  
  if (decisionData.truePath) {
    const truePathNodeId = resolveIdentifierToNodeId(decisionData.truePath, nodes);
    debugLog(`[updateDecisionNodeEdges] True path "${decisionData.truePath}" resolved to node ID: ${truePathNodeId}`);
    if (truePathNodeId) {
      const trueEdge = {
        id: `e${decisionNodeId}-${truePathNodeId}_true`,
        source: decisionNodeId,
        target: truePathNodeId,
        sourceHandle: "true",
        targetHandle: "target",
        type: "custom",
        label: "True",
        style: { stroke: "#52c41a", strokeWidth: 2 },
        labelStyle: { fill: "#52c41a", fontWeight: 600 },
        labelBgStyle: { fill: "#f6ffed" }
      };
      newDecisionEdges.push(trueEdge);
      debugLog(`[updateDecisionNodeEdges] Created true edge: ${trueEdge.id}`);
    }
  }
  
  if (decisionData.falsePath) {
    const falsePathNodeId = resolveIdentifierToNodeId(decisionData.falsePath, nodes);
    debugLog(`[updateDecisionNodeEdges] False path "${decisionData.falsePath}" resolved to node ID: ${falsePathNodeId}`);
    if (falsePathNodeId) {
      const falseEdge = {
        id: `e${decisionNodeId}-${falsePathNodeId}_false`,
        source: decisionNodeId,
        target: falsePathNodeId,
        sourceHandle: "false",
        targetHandle: "target",
        type: "custom",
        label: "False",
        style: { stroke: "#f5222d", strokeWidth: 2 },
        labelStyle: { fill: "#f5222d", fontWeight: 600 },
        labelBgStyle: { fill: "#fff2f0" }
      };
      newDecisionEdges.push(falseEdge);
      debugLog(`[updateDecisionNodeEdges] Created false edge: ${falseEdge.id}`);
    }
  }
  
  const finalEdges = [...nonDecisionNodeEdges, ...newDecisionEdges];
  debugLog(`[updateDecisionNodeEdges] Final edge count: ${finalEdges.length} (${newDecisionEdges.length} new decision edges)`);
  
  // Update edges
  setEdges(finalEdges);
}; 

// Conversion from FlowData to ConversationBuilderRequest for backend API
import { ConversationBuilderSaveRequest, ConversationNode, DecisionConfig, ToolCallFunction } from "../../../../services/ConversationBuilderService";

export const convertFlowDataToConversationBuilderRequest = (
  flowData: FlowData,
  metadata: {
    conversation_flow_name: string;
    company_id: string;
    application_ref_name: string;
    action_type: string;
    conversation_flow_version?: string;
    is_latest?: boolean;
  }
): ConversationBuilderSaveRequest => {
  const { nodes, edges, globalVariableRegistry, interjectionGroups } = flowData.data;
  
  // Convert nodes to ConversationNode format
  const conversationFlow: Record<number, ConversationNode> = {};
  const toolCalls: ToolCallFunction[] = [];
  const decisionNodeIds: number[] = [];
  
  // Filter out placeholder nodes and convert real nodes
  const realNodes = nodes.filter(node => !node.data.isPlaceholder);
  

  
  realNodes.forEach(node => {
    const nodeId = parseInt(node.id, 10);
    

    
    // Convert entityType to role
    let role: string;
    switch (node.data.entityType) {
      case "BOT":
        role = "assistant";
        break;
      case "USER":
        role = "user";
        break;
      case "DECISION":
        role = "assistant";
        decisionNodeIds.push(nodeId);
        break;
      default:
        role = "assistant";
    }
    
    // Handle decision logic conversion first
    let decisionLogic: DecisionConfig | undefined = undefined;
    let nextPossibleNodeIds: number[] = [];
    
    if (node.data.entityType === "DECISION" && node.data.decisionData) {
      const { conditions, truePath, falsePath } = node.data.decisionData;
      

      
      // Convert conditions to JSONLogic format
      const jsonLogic = convertConditionsToJsonLogic(conditions);
      
      // Get actual node IDs for paths
      const trueNodeId = resolvePathToNodeId(truePath, realNodes);
      const falseNodeId = resolvePathToNodeId(falsePath, realNodes);
      

      
      if (trueNodeId && falseNodeId) {
        decisionLogic = {
          passed: trueNodeId,
          failed: falseNodeId,
          logic: jsonLogic
        };
        
        // For decision nodes, ONLY use decision logic node IDs - ignore any regular edges
        // This ensures backend consistency between decision_logic and next_possible_node_ids
        nextPossibleNodeIds = [trueNodeId, falseNodeId];
      } else {
        console.warn(`Decision node ${nodeId} has invalid decision paths - skipping decision logic:`, {
          truePath,
          trueNodeId, 
          falsePath,
          falseNodeId
        });
        // If decision paths are invalid, treat as regular node and use edges
        decisionLogic = undefined;
        nextPossibleNodeIds = getNextNodeIdsFromEdges(node.id, edges);
      }
    } else {
      // For non-decision nodes, get next possible node IDs from edges
      nextPossibleNodeIds = getNextNodeIdsFromEdges(node.id, edges);
    }
    
    // Handle tool calls
    let toolCall: ToolCallFunction | undefined = undefined;
    if (node.data.toolCalls && node.data.toolCalls.length > 0) {
      // Take the first tool call (nodes should have only one)
      const firstToolCall = node.data.toolCalls[0];
      
      // Only create tool call if we have a valid function name
      // Check both 'name' and 'function' properties as the data structure might vary
      const functionName = firstToolCall.name || firstToolCall.function;
      if (functionName && functionName.trim() !== "") {
        // Convert parameters from array format to object format
        const parametersObject: Record<string, any> = {};
        if (firstToolCall.parameters && Array.isArray(firstToolCall.parameters)) {
          firstToolCall.parameters.forEach((param: {name: string, value: string}) => {
            if (param.name && param.name.trim() && param.value) {
              parametersObject[param.name.trim()] = param.value;
            }
          });
        }
        
        // Convert response variables from array format to dictionary format
        const responseVariablesObject: Record<string, any> = {};
        if (firstToolCall.responseVariables && Array.isArray(firstToolCall.responseVariables)) {
          firstToolCall.responseVariables.forEach((responseVar: {name: string, values: string[], persist?: boolean}) => {
            if (responseVar.name && responseVar.name.trim() && responseVar.values && responseVar.values.length > 0) {
              responseVariablesObject[responseVar.name.trim()] = {
                values: responseVar.values,
                persist: responseVar.persist ?? false // Use actual persist value, default to false if undefined
              };
            }
          });
        }
        
        toolCall = {
          name: functionName,
          description: firstToolCall.description || "",
          arguments: JSON.stringify(parametersObject), // Backend expects JSON string
          response_variables: responseVariablesObject // Add response variables as dictionary
        } as ToolCallFunction;
        
        // Also add to global tool calls list
        if (!toolCalls.some(tc => tc.name === toolCall!.name)) {
          toolCalls.push(toolCall);
        }
      } else {
        console.warn(`Node ${nodeId} has tool call with empty function name, skipping:`, firstToolCall);
      }
    }
    
    // Handle variables from decision variables registry
    const variables: Record<string, any> = {};
    if (node.data.variableAssignments) {
      node.data.variableAssignments.forEach((assignment: VariableAssignment) => {
        if (assignment.variableName && assignment.value !== undefined && assignment.value !== null) {
          variables[assignment.variableName] = assignment.value;
        }
      });
    }
    
    // Determine if start/end node
    // Check if node is explicitly marked as start node or is the first node
    const isFirstNode = realNodes[0]?.id === node.id;
    const isStartNode = node.data.isStartNode || node.data.potentialStart || isFirstNode;
    const isEndNode = nextPossibleNodeIds.length === 0;
    
    // Ensure content is not empty
    const nodeContent = node.data.content && node.data.content.trim() !== "" 
      ? node.data.content 
      : `Node ${nodeId} content`;
    
    conversationFlow[nodeId] = {
      role,
      content: nodeContent,
      is_start_node: isStartNode,
      is_end_node: isEndNode,
      category: node.data.identifier || nodeContent.toUpperCase(),
      decision_logic: decisionLogic,
      next_possible_node_ids: nextPossibleNodeIds,
      variables: Object.keys(variables).length > 0 ? variables : undefined,
      tool_call: toolCall
    };
  });
  
  // Add system prompt as assistant node with MAITAI_PREFIX if system prompt exists
  if (flowData.data.systemPrompt && flowData.data.systemPrompt.trim()) {
    // Find the current start node
    const currentStartNodeId = Object.keys(conversationFlow).find(id => 
      conversationFlow[parseInt(id)].is_start_node
    );
    
    // If there's a current start node, make it not the start node anymore
    if (currentStartNodeId) {
      conversationFlow[parseInt(currentStartNodeId)].is_start_node = false;
    }
    
    // Create system prompt node as assistant with MAITAI_PREFIX and ID 0 (lowest possible)
    const systemNodeId = 0;
    conversationFlow[systemNodeId] = {
      role: "assistant",
      content: `MAITAI_PREFIX${flowData.data.systemPrompt.trim()}`,
      is_start_node: true,
      is_end_node: false,
      category: "MAITAI_SYSTEM_PROMPT",
      next_possible_node_ids: currentStartNodeId ? [parseInt(currentStartNodeId)] : [],
      variables: undefined,
      tool_call: undefined
    };
  }
  
  // Convert global variable registry to placeholder variables
  const placeholderVariables: Record<string, { values: string[]; persist?: boolean }> = {};
  Object.entries(globalVariableRegistry).forEach(([key, variable]) => {
    if (variable.values && variable.values.length > 0) {
      placeholderVariables[key] = {
        values: variable.values,
        persist: variable.persist || false
      };
    }
  });
  
  // Convert interjection groups to interjections
  const interjections: Record<string, any> = {};
  interjectionGroups.forEach(group => {
    // Use the group name as the dictionary key
    const dictionaryKey = group.groupName || group.id;
    
    // Determine the interjection type based on the group type
    let interjectionType: string;
    if (group.type === "other" && group.customTypeName) {
      interjectionType = group.customTypeName;
    } else {
      interjectionType = group.type.toUpperCase();
    }
    
    // Map over content items to add the required interjection_type field
    const contentWithType = group.content.map(contentItem => ({
      interjection_text: contentItem.interjection_text,
      interjection_type: interjectionType,
      interjection_response: contentItem.interjection_response
    }));
    
    interjections[dictionaryKey] = {
      interjection_type: interjectionType,
      content: contentWithType,
      interjection_rate: group.interjection_rate,
      within_script: group.within_script,
      repeat_script: group.repeat_script
    };
  });
  
  // Build configs - determine start node based on whether system prompt exists
  let startNodeIds: number[] | undefined;
  if (flowData.data.systemPrompt && flowData.data.systemPrompt.trim()) {
    // If system prompt exists, start node is 0 (MAITAI_PREFIX node)
    startNodeIds = [0];
  } else {
    // Otherwise use the first real node
    startNodeIds = realNodes.length > 0 ? [parseInt(realNodes[0].id)] : undefined;
  }
  
  const configs = {
    start_node: startNodeIds,
    end_node: realNodes.filter(node => getNextNodeIdsFromEdges(node.id, edges).length === 0)
                     .map(node => parseInt(node.id, 10)),
    decision_node_id: decisionNodeIds.length > 0 ? decisionNodeIds : undefined,
    buildup: false,
    deduplicate: true,
    canonical: true
  };
  
  // Validate that we have at least one conversation node
  if (Object.keys(conversationFlow).length === 0) {
    throw new Error("No valid conversation nodes found. Please add at least one BOT, USER, or DECISION node.");
  }

  // Extract node positions for storage in metadata
  const nodePositions: Record<string, { x: number; y: number }> = {};
  realNodes.forEach(node => {
    nodePositions[node.id] = {
      x: node.position.x,
      y: node.position.y
    };
  });
  
  console.log("[DEBUG] Saving node positions to backend metadata:", nodePositions);

  const result = {
    conversation_flow: conversationFlow,
    placeholder_variables: placeholderVariables,
    configs,
    interjections,
    tool_calls: toolCalls,
    metadata: {
      ...flowData.metadata,
      nodePositions // Add node positions to metadata
    },
    agent_id: 1, // Hardcoded agent_id to fix database parameter issue
    ...metadata
  };



  return result;
};

// Helper function to get next node IDs from edges
const getNextNodeIdsFromEdges = (nodeId: string, edges: Edge[]): number[] => {
  return edges
    .filter(edge => edge.source === nodeId)
    .map(edge => parseInt(edge.target))
    .filter(id => !isNaN(id));
};

// Helper function to resolve path (identifier or node ID) to actual node ID
const resolvePathToNodeId = (path: string, nodes: Node[]): number | null => {
  if (!path) {
    return null;
  }
  
  // First try as direct node ID
  const directNodeId = parseInt(path);
  if (!isNaN(directNodeId)) {
    const foundNode = nodes.find(n => parseInt(n.id) === directNodeId);
    if (foundNode) {
      return directNodeId;
    }
  }
  
  // Then try as identifier
  const nodeByIdentifier = nodes.find(n => n.data.identifier === path);
  if (nodeByIdentifier) {
    const resolvedId = parseInt(nodeByIdentifier.id);
    return resolvedId;
  }
  
  return null;
};

// Helper function to convert frontend conditions to JSONLogic format
const convertConditionsToJsonLogic = (
  conditions: Array<{ variable: string; operator: string; value: any }>
): any => {
  if (!conditions || conditions.length === 0) {
    return { "==": [true, true] }; // Default to always true
  }
  
  if (conditions.length === 1) {
    const condition = conditions[0];
    return convertSingleConditionToJsonLogic(condition);
  }
  
  // Multiple conditions - combine with AND
  const jsonLogicConditions = conditions.map(convertSingleConditionToJsonLogic);
  return { "and": jsonLogicConditions };
};

// Helper function to convert a single condition to JSONLogic
const convertSingleConditionToJsonLogic = (condition: { variable: string; operator: string; value: any }): any => {
  const { variable, operator, value } = condition;
  
  switch (operator) {
    case "==":
    case "equals":
      return { "==": [{ "var": variable }, value] };
    case "!=":
    case "not_equals":
      return { "!=": [{ "var": variable }, value] };
    case ">":
    case "greater_than":
      return { ">": [{ "var": variable }, value] };
    case "<":
    case "less_than":
      return { "<": [{ "var": variable }, value] };
    case ">=":
    case "greater_than_or_equal":
      return { ">=": [{ "var": variable }, value] };
    case "<=":
    case "less_than_or_equal":
      return { "<=": [{ "var": variable }, value] };
    case "contains":
      return { "in": [value, { "var": variable }] };
    case "not_contains":
      return { "!": { "in": [value, { "var": variable }] } };
    case "starts_with":
      // For now, fall back to basic contains check until proper string manipulation is supported
      return { "in": [value, { "var": variable }] };
    case "ends_with":
      // For now, fall back to basic contains check until proper string manipulation is supported
      return { "in": [value, { "var": variable }] };
    case "is_true":
      return { "==": [{ "var": variable }, true] };
    case "is_false":
      return { "==": [{ "var": variable }, false] };
    case "is_null":
      // Simple null check - just check for null
      return { "==": [{ "var": variable }, null] };
    case "is_not_null":
      // Simple not null check - just check for not null
      return { "!=": [{ "var": variable }, null] };
    default:
      return { "==": [{ "var": variable }, value] };
  }
}; 

// Generate a unique random identifier
export const generateUniqueIdentifier = (existingIdentifiers: string[]): string => {
  let attempts = 0;
  const maxAttempts = 1000; // Increased attempts since we're just generating numbers
  
  while (attempts < maxAttempts) {
    // Generate a random number between 100000 and 999999 (6 digits)
    const randomNumber = Math.floor(Math.random() * 900000) + 100000;
    const identifier = randomNumber.toString();
    
    if (!existingIdentifiers.includes(identifier)) {
      return identifier;
    }
    attempts++;
  }
  
  // If we somehow can't find a unique 6-digit number, try 7 digits
  for (let i = 0; i < maxAttempts; i++) {
    const randomNumber = Math.floor(Math.random() * 9000000) + 1000000; // 7 digits
    const identifier = randomNumber.toString();
    
    if (!existingIdentifiers.includes(identifier)) {
      return identifier;
    }
  }

  // Fallback to timestamp-based identifier (extremely unlikely)
  return `id_${Date.now()}`;
}; 